import { useEffect, useId, useRef, useState } from 'react';
import { COMPANY } from '@/content/company';
import { revealPhone, revealedPhone, type Phone } from '@/lib/phone';
import { Spinner } from './ui';
import styles from './PhoneReveal.module.css';

/**
 * The telephone number, behind one click.
 *
 * The number is not in this site's source - see the note in src/content/company.ts
 * and the mechanism in src/lib/phone.ts. What ships is a masked prefix and a
 * control; the real value arrives from the backend after a server-side reCAPTCHA
 * check.
 *
 * TWO CONSTRAINTS ON WHERE THIS MAY BE USED
 *
 * 1. Only on a route whose per-route CSP permits Google. That is /contact alone -
 *    see the `recaptcha` flag in scripts/prerender.mjs. Rendering this anywhere
 *    else produces a button that can never succeed, because the captcha script
 *    would be blocked.
 * 2. Only on a page that already carries the reCAPTCHA attribution. Google's
 *    terms allow the floating badge to be hidden only if the attribution appears
 *    in the page instead, and the badge is hidden site-wide by the
 *    .grecaptcha-badge rule in global.css. On /contact the enquiry form's footer
 *    carries it, which covers this control on the same page; a second copy in
 *    this card would be noise.
 *
 * Both are the same constraint from different directions: this belongs on
 * /contact. Putting it on a second route means widening that route's CSP and
 * rewriting the "the contact page is the single exception" disclosure that
 * appears in nine places across the site.
 */

type Status = 'idle' | 'checking' | 'error';

export function PhoneReveal() {
  /*
   * Seeded from the module-level cache so returning to this page in the same
   * session does not ask the visitor to verify twice. Null during the build-time
   * prerender and on a first load, so the server and client markup agree.
   */
  const [phone, setPhone] = useState<Phone | null>(() => revealedPhone());
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const linkRef = useRef<HTMLAnchorElement>(null);
  /*
   * True only for a reveal this visitor just asked for. Focus is moved to the
   * number then - the control they were on has been replaced, so leaving focus
   * where it was would strand it on a detached element - but not when the number
   * is restored from the cache on a fresh mount, where stealing focus would be an
   * unexplained jump on a page nobody has interacted with yet.
   */
  const justRevealed = useRef(false);

  /*
   * Focus moves here rather than in the callback that set the number.
   *
   * A setTimeout after setState looks equivalent and is not: pressing the button
   * removes it from the document, and the browser's own "the focused element is
   * gone, reset to body" runs asynchronously. A focus() scheduled as a macrotask
   * loses that race about as often as it wins it - the a11y suite caught it
   * failing in all three browsers while jsdom was perfectly happy. An effect runs
   * in the same commit as the removal, so there is no window for the reset to
   * land in.
   */
  useEffect(() => {
    if (phone && justRevealed.current) {
      justRevealed.current = false;
      linkRef.current?.focus();
    }
  }, [phone]);

  const errorId = useId();
  const checking = status === 'checking';

  const handleClick = () => {
    // aria-disabled rather than `disabled` keeps the button focusable while it
    // works, so a keyboard visitor is not thrown back to the top of the document
    // mid-request. It also means the guard has to be here.
    if (checking) return;

    setStatus('checking');
    setError(null);

    void revealPhone().then((result) => {
      if (result.ok) {
        // Read by the effect above, which is what actually moves focus.
        justRevealed.current = true;
        setPhone(result.phone);
        setStatus('idle');
      } else {
        setError(result.error);
        setStatus('error');
      }
    });
  };

  if (phone) {
    /*
     * No live region here on purpose. Focus has just been moved onto this link,
     * which makes a screen reader read the number out - the announcement and the
     * useful next action in one. A status message beside it would say the same
     * thing twice.
     */
    return (
      <a ref={linkRef} href={`tel:${phone.href}`} className={`${styles.number} sk-hit`}>
        {phone.display}
      </a>
    );
  }

  return (
    <>
      <span className={styles.mask} aria-hidden="true">
        {COMPANY.phoneMask}
      </span>{' '}
      {/*
       * No .sk-hit here, unlike the revealed link below. That utility pads a
       * standalone control up to the 24px pointer target of WCAG 2.2 SC 2.5.8,
       * and its own comment in global.css warns against using it on something
       * sitting inside a sentence - which this is. The criterion's Inline
       * exception covers it.
       */}
      <button
        type="button"
        className={styles.button}
        onClick={handleClick}
        aria-disabled={checking || undefined}
        aria-describedby={error ? errorId : undefined}
      >
        {checking ? (
          <>
            <Spinner /> Checking…
          </>
        ) : error ? (
          'Try again'
        ) : (
          'Show phone number'
        )}
      </button>
      {/*
       * The visible, and only, announcement of a failure. role="alert" is
       * assertive because the visitor pressed a button and is waiting on the
       * answer - a polite region could sit unread behind whatever else is
       * speaking.
       */}
      {error && (
        <span className={styles.error} id={errorId} role="alert">
          {error}
        </span>
      )}
      {/*
       * The wait, for anyone not watching the spinner. Emptied when it ends
       * rather than announcing an outcome, which is either the focus move above
       * or the alert.
       *
       * Mounted even while idle, deliberately: a live region inserted at the same
       * moment as its content is missed by some assistive technology, so the
       * region has to be there first and only its text may change.
       */}
      <span className="sk-visually-hidden" role="status">
        {checking ? 'Checking that you are a person…' : ''}
      </span>
      {/*
       * With scripting off, the button above is inert - the page is prerendered, so
       * it renders and simply cannot do anything. Saying so beats leaving someone
       * clicking a control that will never answer.
       */}
      <noscript>
        {' '}
        <span className={styles.noscript}>
          Showing the number needs JavaScript. Email{' '}
          <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> and we will call you back.
        </span>
      </noscript>
    </>
  );
}
